import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Save,
  Cancel,
  Add,
  Delete,
  CloudUpload,
  AttachFile,
  SwapHoriz,
} from '@mui/icons-material';
import { Account, JournalLine, Currency } from '../../types';
import api from '../../config/api';
import { format } from 'date-fns';

export const ExpenseForm: React.FC = () => {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [entryDate, setEntryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [currencyId, setCurrencyId] = useState<number>(1); // Default to EUR
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [journalLines, setJournalLines] = useState<JournalLine[]>([
    { account_id: 0, debit_amount: 0, credit_amount: 0, description: '' },
    { account_id: 0, debit_amount: 0, credit_amount: 0, description: '' },
  ]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [baseCurrency, setBaseCurrency] = useState<Currency | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [journalEntryId, setJournalEntryId] = useState<number | null>(null);

  useEffect(() => {
    fetchAccounts();
    fetchCurrencies();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounting/accounts', { params: { is_active: true } });
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await api.get('/currencies', { params: { is_active: true } });
      setCurrencies(response.data);
      
      // Find base currency (EUR)
      const base = response.data.find((c: Currency) => c.is_base);
      setBaseCurrency(base);
      if (base) {
        setCurrencyId(base.id);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const handleCurrencyChange = async (newCurrencyId: number) => {
    setCurrencyId(newCurrencyId);
    
    // If base currency, set rate to 1
    if (baseCurrency && newCurrencyId === baseCurrency.id) {
      setExchangeRate(1);
      return;
    }
    
    // Exchange rate conversion endpoint not yet implemented
    // Default to 1.0 - user can manually enter rate
    setExchangeRate(1.0);
  };

  const handleAddLine = () => {
    setJournalLines([
      ...journalLines,
      { account_id: 0, debit_amount: 0, credit_amount: 0, description: '' },
    ]);
  };

  const handleRemoveLine = (index: number) => {
    if (journalLines.length > 2) {
      setJournalLines(journalLines.filter((_, i) => i !== index));
    }
  };

  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...journalLines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: field.includes('amount') ? parseFloat(value) || 0 : value,
    };
    setJournalLines(updatedLines);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadedFiles(Array.from(e.target.files));
    }
  };

  const calculateTotals = () => {
    const totalDebit = journalLines.reduce((sum, line) => sum + line.debit_amount, 0);
    const totalCredit = journalLines.reduce((sum, line) => sum + line.credit_amount, 0);
    return { totalDebit, totalCredit, isBalanced: totalDebit === totalCredit };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { isBalanced } = calculateTotals();
    if (!isBalanced) {
      setError('Journal entry must balance (debits must equal credits)');
      setLoading(false);
      return;
    }

    try {
      // Create journal entry
      const response = await api.post('/accounting/journal-entries', {
        entry_date: entryDate,
        description,
        currency_id: currencyId,
        exchange_rate: exchangeRate,
        journal_lines: journalLines.filter(line => line.account_id > 0),
      });

      const entryId = response.data.id;
      setJournalEntryId(entryId);

      // Upload attachments if any
      // Note: Attachment upload endpoint may need to be implemented
      // For now, skip uploads or use basic upload endpoint
      if (uploadedFiles.length > 0) {
        try {
          // Try basic upload endpoint (one file at a time)
          for (const file of uploadedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            await api.post('/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          // Don't fail the whole form submission if upload fails
        }
      }

      navigate('/expenses');
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const { totalDebit, totalCredit, isBalanced } = calculateTotals();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Record New Expense
      </Typography>

      <Paper elevation={2} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 22%" } }}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 22%" } }}>
              <TextField
                select
                fullWidth
                label="Currency"
                value={currencyId}
                onChange={(e) => handleCurrencyChange(Number(e.target.value))}
                required
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code} - {currency.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 22%" } }}>
              <TextField
                fullWidth
                label={`Exchange Rate to ${baseCurrency?.code || 'EUR'}`}
                type="number"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(Number(e.target.value))}
                InputProps={{
                  inputProps: { step: 0.000001, min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SwapHoriz />
                    </InputAdornment>
                  ),
                }}
                required
                disabled={baseCurrency ? currencyId === baseCurrency.id : false}
              />
            </Box>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 22%" } }}>
              <TextField
                fullWidth
                label="Base Amount Preview"
                value={(calculateTotals().totalDebit * exchangeRate).toFixed(2)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      {baseCurrency?.symbol || '€'}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 100%" }}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                multiline
                rows={2}
              />
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Journal Entries
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalLines.map((line, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={line.account_id}
                        onChange={(e) => handleLineChange(index, 'account_id', e.target.value)}
                        required
                      >
                        <MenuItem value={0}>Select Account</MenuItem>
                        {accounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.account_code} - {account.account_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={line.debit_amount || ''}
                        onChange={(e) => handleLineChange(index, 'debit_amount', e.target.value)}
                        InputProps={{ inputProps: { step: 0.01 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={line.credit_amount || ''}
                        onChange={(e) => handleLineChange(index, 'credit_amount', e.target.value)}
                        InputProps={{ inputProps: { step: 0.01 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveLine(index)}
                        disabled={journalLines.length <= 2}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} align="right">
                    <strong>Total:</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{currencies.find(c => c.id === currencyId)?.symbol || ''}{totalDebit.toFixed(2)}</strong>
                    {baseCurrency && currencyId !== baseCurrency.id && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {baseCurrency.symbol}{(totalDebit * exchangeRate).toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <strong>{currencies.find(c => c.id === currencyId)?.symbol || ''}{totalCredit.toFixed(2)}</strong>
                    {baseCurrency && currencyId !== baseCurrency.id && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {baseCurrency.symbol}{(totalCredit * exchangeRate).toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isBalanced ? (
                      <Chip label="Balanced" color="success" size="small" />
                    ) : (
                      <Chip label="Unbalanced" color="error" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            startIcon={<Add />}
            onClick={handleAddLine}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            Add Line
          </Button>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Attachments
          </Typography>

          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
            >
              Upload Receipt Images
              <input
                type="file"
                hidden
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileSelect}
              />
            </Button>
            {uploadedFiles.length > 0 && (
              <Box mt={2}>
                {uploadedFiles.map((file, index) => (
                  <Chip
                    key={index}
                    icon={<AttachFile />}
                    label={file.name}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box mt={3} display="flex" gap={2}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading || !isBalanced}
            >
              {loading ? 'Saving...' : 'Save Expense'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate('/expenses')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};
